/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef, useState } from 'react';
import { Cross, Eye, HideColumn } from '@saleshandy/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  OverlayPopover,
  OverlayTooltip,
  Placement,
} from '../../../../shared/design-system/components/overlay';
import { DotsVerticals } from '../../../../shared/svg';
import { SortableColumnFieldProps } from '../draggable-input-modal/type';
import Input from '../../../../shared/design-system/components/input';
import { accessibleOnClick } from '../../../../shared/utils';
import { capitalize } from '../../../../shared/utils/string';
import { CATEGORY_VALUE_LABEL_MAP } from '../../components/prospect-kanban/helper';

const SortableColumnField: React.FC<SortableColumnFieldProps> = ({
  column,
  onChange,
  onRemove,
  disableRemove,
  shouldFocusOnMount = false,
  onToggleColorPicker,
  onColorChange,
  isColorPickerOpen,
  availableColors,
  isDuplicate,
  isColorSelectionEnabled,
  errorMessage,
  isHidden,
  isVisibilityToggleEnabled,
  onToggleVisibility,
  disableVisibilityToggle = false,
  isDragDisabled,
  isNew,
  showColorSwatch = true,
  removeIcon = <Cross />,
  removeTooltip = 'Remove',
  isDisabled = false,
  onAddNewOption,
  handleAddColumn,
  selectedCategoryName,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: column.id,
    disabled: isDragDisabled,
  });

  const style = {
    transition,
    transform: CSS.Transform.toString(transform),
  };

  const [hasError, setHasError] = useState(false);

  const colorOptions = availableColors ?? [];
  const dragHandleProps = isDragDisabled ? {} : { ...attributes, ...listeners };
  const dragButtonAriaProps = isDragDisabled
    ? { 'aria-disabled': true, tabIndex: -1 }
    : {};

  const showVisibilityToggle = isVisibilityToggleEnabled && !isNew;
  const colorPickerRef = useRef<HTMLDivElement>(null);

  // Focus this column's input when modal was opened from this lane
  useEffect(() => {
    if (shouldFocusOnMount && inputRef.current) {
      const timer = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [shouldFocusOnMount]);

  // Close popover on scroll
  useEffect(() => {
    if (!isColorPickerOpen) return;

    const closeColorPicker = () => {
      onToggleColorPicker?.(column.id);
    };

    const handleScroll = () => {
      closeColorPicker();
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      const triggerContainer = colorPickerRef.current;
      const popoverElement = document.querySelector(
        '.categorize-by-modal-popover',
      ) as HTMLElement | null;

      const isClickOnTrigger = triggerContainer?.contains(target);
      const isClickOnPopover = popoverElement?.contains(target);

      if (!isClickOnTrigger && !isClickOnPopover) {
        closeColorPicker();
      }
    };

    // Scroll listeners (capture phase to catch nested scrolls)
    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('scroll', handleScroll, true);

    // Click outside listener
    document.addEventListener('mousedown', handleClickOutside);

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isColorPickerOpen, column.id, onToggleColorPicker]);

  const onColumnNameChange = (value: string) => {
    onChange(column?.id, value || '');
    if (value.trim().length > 28) {
      setHasError(true);
    } else {
      setHasError(false);
    }
  };

  return (
    <div
      className={`categorize-by-modal__item ${
        isDragging ? 'is-dragging' : ''
      } ${isHidden ? 'is-hidden' : ''}`}
      ref={setNodeRef}
      style={style}
    >
      <div className="categorize-by-modal__item-content">
        <button
          type="button"
          className="categorize-by-modal__drag-handle"
          {...dragHandleProps}
          {...dragButtonAriaProps}
        >
          <DotsVerticals />
        </button>

        <>
          {isColorSelectionEnabled ? (
            <>
              <div ref={colorPickerRef}>
                <OverlayPopover
                  show={isColorPickerOpen}
                  placement={Placement.AutoEnd}
                  content={
                    <div className="categorize-by-modal__color-palette categorize-by-modal__color-picker">
                      {colorOptions?.map((color) => (
                        <button
                          key={color?.fill}
                          type="button"
                          className={`categorize-by-modal__color-option${
                            column.color === color.fill ? ' is-selected' : ''
                          }`}
                          style={{
                            backgroundColor: color.fill,
                            borderColor: color.border,
                          }}
                          {...accessibleOnClick((event) => {
                            if (isHidden) {
                              return;
                            }
                            event.stopPropagation();
                            event.preventDefault();
                            onColorChange?.(column.id, color);
                          })}
                          onPointerDown={(event) => event.stopPropagation()}
                          disabled={isHidden}
                          aria-label={`Select color ${color.fill}`}
                        />
                      ))}
                    </div>
                  }
                  className="categorize-by-modal-popover"
                  popperConfig={{
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 8],
                        },
                      },
                      {
                        name: 'flip',
                        options: {
                          fallbackPlacements: ['top-start'],
                          allowedAutoPlacements: ['bottom-start'],
                        },
                      },
                    ],
                  }}
                >
                  <OverlayTooltip text="Change Color">
                    <button
                      type="button"
                      className="categorize-by-modal__color-swatch categorize-by-modal__color-swatch--trigger"
                      style={{
                        backgroundColor: column.color,
                        borderColor: column.borderColor,
                      }}
                      disabled={isHidden}
                      onClick={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        onToggleColorPicker?.(column?.id);
                      }}
                      onPointerDown={(event) => event.stopPropagation()}
                      aria-label="Choose color"
                    />
                  </OverlayTooltip>
                </OverlayPopover>
              </div>
            </>
          ) : (
            <span
              className="categorize-by-modal__color-swatch categorize-by-modal__color-swatch--disabled"
              style={{
                backgroundColor: column.color,
                borderColor: column.borderColor,
              }}
              aria-hidden="true"
            />
          )}
        </>

        {selectedCategoryName?.group === 'system' ? (
          <OverlayTooltip text="System columns can’t be renamed.">
            <div className="d-flex flex-column">
              <Input
                ref={inputRef}
                name={`categorize-by-${column.id}`}
                className={`categorize-by-modal__input ${
                  hasError ? 'input-error categorize-by-modal__input' : ''
                }`}
                placeholder="Enter column name"
                value={
                  isDisabled && column.value
                    ? capitalize(
                        CATEGORY_VALUE_LABEL_MAP[column?.value] ??
                          column?.value,
                      )
                    : CATEGORY_VALUE_LABEL_MAP[column?.value] ?? column?.value
                }
                onChange={(value) => onColumnNameChange(value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (handleAddColumn) {
                      handleAddColumn();
                    }
                    if (onAddNewOption) {
                      onAddNewOption();
                    }
                  }
                }}
                variant={isDuplicate ? Input.Variant.Error : undefined}
                disabled={isHidden || isDisabled}
                autoFocus={shouldFocusOnMount}
              />
              {hasError && (
                <span className="error-text">
                  Column name should be less than 28 characters
                </span>
              )}
            </div>
          </OverlayTooltip>
        ) : (
          <div className="d-flex flex-column">
            <Input
              ref={inputRef}
              name={`categorize-by-${column.id}`}
              className={`categorize-by-modal__input ${
                hasError ? 'input-error categorize-by-modal__input' : ''
              }`}
              placeholder="Enter column name"
              value={
                isDisabled && column.value
                  ? capitalize(
                      CATEGORY_VALUE_LABEL_MAP[column?.value] ?? column?.value,
                    )
                  : CATEGORY_VALUE_LABEL_MAP[column?.value] ?? column?.value
              }
              onChange={(value) => onColumnNameChange(value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (handleAddColumn) {
                    handleAddColumn();
                  }
                  if (onAddNewOption) {
                    onAddNewOption();
                  }
                }
              }}
              variant={isDuplicate ? Input.Variant.Error : undefined}
              disabled={isHidden || isDisabled}
              autoFocus={shouldFocusOnMount}
            />
            {hasError && (
              <span className="error-text">
                Column name should be less than 28 characters
              </span>
            )}
          </div>
        )}

        {showVisibilityToggle ? (
          <OverlayTooltip
            text={
              disableVisibilityToggle
                ? 'At least one column must be visible'
                : isHidden
                ? 'Show Column'
                : 'Hide Column'
            }
          >
            <button
              type="button"
              className={`categorize-by-modal__visibility-toggle${
                isHidden ? ' is-hidden' : ''
              }${disableVisibilityToggle ? ' disabled' : ''}`}
              {...(disableVisibilityToggle
                ? {}
                : accessibleOnClick(() => onToggleVisibility?.(column?.id)))}
              onPointerDown={(event) => event.stopPropagation()}
              aria-pressed={isHidden}
              aria-label={isHidden ? 'Unhide column' : 'Hide column'}
              disabled={disableVisibilityToggle}
            >
              {isHidden ? <HideColumn /> : <Eye />}
            </button>
          </OverlayTooltip>
        ) : !disableRemove ? (
          <OverlayTooltip text={removeTooltip}>
            <button
              type="button"
              className="categorize-by-modal__remove"
              {...accessibleOnClick(() => onRemove(column?.id))}
              onPointerDown={(event) => event.stopPropagation()}
            >
              {removeIcon}
            </button>
          </OverlayTooltip>
        ) : null}
      </div>
      {isDuplicate && errorMessage ? (
        <span className="categorize-by-modal__error">{errorMessage}</span>
      ) : null}
    </div>
  );
};

export default SortableColumnField;
